import React, { useState, useEffect, useContext, useCallback } from 'react'
import { Form } from 'antd'
import { useTranslation } from 'react-i18next'
import {
  ADD_RESPONSE,
  REMOVE_RESPONSE,
  UPDATE_RESPONSE,
  multiCourse
} from '@BUP/constants'
import { findAnswerByHasExternalList } from '@BUP/services/BraveUp/Sociogram'
import styles from './questions-list.module.scss'
import UsersGroup from '@BUP/assets/Icons/users_group.svg'
import SubtitleDashboard from '@BUP/components/Generics/SubtitleDashboard'
import SubjectGroupCard from '@BUP/components/Generics/Cards/SubjectGroupCard'
import QuestionElement from '../QuestionElement'
import AlertComponent from '@BUP/components/Generics/Alert'
import ButtonComponent from '@BUP/components/Generics/Buttons/button-component'
import { useSociogramState } from '@BUP/hooks/Sociogram/useSociogramContext'
import { EnrollmentContext } from '@BUP/contexts/EnrollmentContext'
import { OrganizationContext } from '@BUP/contexts/OrganizationContext'
import { useSelectedResponses } from '@BUP/hooks/Sociogram/useSelectedResponses'
import { useSurveyQuestionsByPage } from '@BUP/hooks/Sociogram/useSurveyQuestionsByPage'
import BupSpinner from '@BUP/components/Loading/Spinner'

const QuestionsList = ({
  onSubmit,
  classMates,
  courses,
  groupCoursesData,
  errorCreateResponse,
  errorParticipantsTerminated,
  page,
  surveyId
}) => {
  const [t] = useTranslation('translation', {
    keyPrefix: 'pages.data.answerSociogram'
  })
  const enrollment = useContext(EnrollmentContext)
  const enrollmentId = enrollment && enrollment.id

  const [, forceUpdate] = useState()
  const { form } = useSociogramState()
  const { selectedResponses, dispatchSelectedResponses } =
  useSelectedResponses()

  const organization = useContext(OrganizationContext)
  const { type: organizationType } = organization

  const {
    isLoading: isLoadingQuestions,
    error: errorQuestions,
    data: questions
  } = useSurveyQuestionsByPage(surveyId, page)

  useEffect(() => {
    forceUpdate({})
    form.resetFields()
  }, [form])

  const filterResponses = selectedResponses?.filter(
    elem => elem?.question?.question_textId?.showIfAnswerSelectedIs !== null
  )

  // maneja cada pregunta independiente del tipo, si es padre o hija
  const handleChangeQuestionResponse = useCallback((
    name,
    question,
    answerId,
    values,
    isIncremental = false
  ) => {
    const questionType = question.question_textId.type
    const questionName =
      questionType === 'check' ? `${name}-checkId-${answerId}` : name

    if (answerId) {
      const filterAnswer = question.question_textId.answers.find(
        answer => answer.id === answerId
      )
      if (questionType === 'check') {
        const hasResponse = selectedResponses?.some(
          element => element.responseId === questionName
        )

        if (!isIncremental) {
          if (!hasResponse) {
            dispatchSelectedResponses({
              type: ADD_RESPONSE,
              payload: {
                ...filterAnswer,
                responseId: questionName,
                questionType: question.question_textId.type,
                isChildren: question.isChildren,
                question,
                content: values && values?.[0] ? [...values] : [],
                answerId
              }
            })
          } else {
            form.resetFields([questionName])
            dispatchSelectedResponses({
              type: REMOVE_RESPONSE,
              payload: {
                responseId: questionName
              }
            })
          }
        } else {
          if (!hasResponse) {
            dispatchSelectedResponses({
              type: ADD_RESPONSE,
              payload: {
                ...filterAnswer,
                responseId: questionName,
                questionType: question.question_textId?.type,
                isChildren: question.isChildren,
                question,
                content: values && values?.[0] ? [...values] : [],
                answerId
              }
            })
          } else {
            dispatchSelectedResponses({
              type: UPDATE_RESPONSE,
              payload: {
                ...filterAnswer,
                responseId: questionName,
                questionType: question.question_textId.type,
                isChildren: question.isChildren,
                question,
                content: values && values?.[0] ? [...values] : [],
                answerId
              }
            })
          }
        }
      } else {
        dispatchSelectedResponses({
          type: ADD_RESPONSE,
          payload: {
            ...filterAnswer,
            responseId: questionName,
            questionType: question.question_textId.type,
            isChildren: question.isChildren,
            question,
            content: values && values?.[0] ? [...values] : [],
            answerId
          }
        })
      }
    }
    if (values) {
      if (!values.length) form.setFieldsValue({ [questionName]: undefined })
      const questionDependencies = question.dependencies
      questionDependencies.forEach(currentDependency => {
        const dependencyType = currentDependency?.question_textId?.type
        const associatesResponses = selectedResponses.filter(
          response =>
            response.isChildren === true &&
            response?.question?.question_textId?.id ===
              currentDependency?.question_textId?.id
        )
        const mustRemoveResponses = values.reduce(
          (acc, value) => {
            const classMate = value
            const filteredResponses = acc.filter(response => {
              const dependencyId =
                dependencyType === 'check'
                  ? `dependency-${question.question_textId.id}-question-${response?.question.question_textId.id}-classmate-${classMate?.enrollmentId}-checkId-${response.id}`
                  : `dependency-${question.question_textId.id}-question-${response?.question.question_textId.id}-classmate-${classMate?.enrollmentId}`
              return response.responseId !== dependencyId
            })
            return filteredResponses
          },
          [...associatesResponses]
        )
        if (mustRemoveResponses.length) {
          if (dependencyType === 'check') {
            const forceResetField = `dependency-${question.question_textId?.id}-question-${currentDependency?.question_textId?.id}-classmate-${mustRemoveResponses[0]?.content[0]?.enrollmentId}`
            form.resetFields([forceResetField])
          }
          mustRemoveResponses.forEach(response => {
            form.resetFields([response.responseId])
            dispatchSelectedResponses({
              type: REMOVE_RESPONSE,
              payload: {
                responseId: response.responseId
              }
            })
          })
        }
      })
    }
  }, [dispatchSelectedResponses, selectedResponses, form])

  // setea como valor inicial las preguntas padres que tengan como opcion "nadie", en el state selectedResponses
  const setQuestionsWhitSelect = useCallback(arrQuestions => {
    arrQuestions?.forEach(question => {
      if (question.question_textId) {
        const nothingAnswer = findAnswerByHasExternalList(
          question.question_textId?.answers,
          false
        )
        handleChangeQuestionResponse(
          `question-${question.question_textId.id}`,
          question,
          nothingAnswer?.id
        )
      }
    })
  }, [handleChangeQuestionResponse])

  useEffect(() => {
    setQuestionsWhitSelect(questions)
  }, [])

  if (isLoadingQuestions) return <BupSpinner size='large' />
  if (!questions) return null
  if (errorQuestions) {
    return (
      <div className={styles.alert_wrapper}>
        <AlertComponent messageKey='common.resources.survey-questions.actions.read.failure' error={errorQuestions} type='error' />
      </div>
    )
  }

  if (questions.length === 0 || null) {
    return (
      <div className={styles.no_questions__wrapper}>
        <h2>{t('noQuestions')}</h2>
      </div>
    )
  }

  const pageId = questions[0].page_id?.id

  questions.sort((obj1, obj2) => {
    if (obj1.position < obj2.position) {
      return -1
    }
    if (obj1.position > obj2.position) {
      return 1
    }
    return 0
  })

  return (
    <div className={styles.container}>
      <SubtitleDashboard
        icon={UsersGroup}
        text={questions[0].page_id?.name}
      />
      {organizationType !== multiCourse
        ? null
        : pageId === 12 ||
        pageId === 18 ||
        pageId === 13 ||
        pageId === 19
          ? (
            <SubjectGroupCard
              title={t('subjectGroupCardTitle')}
              groupCoursesData={groupCoursesData}
              courses={courses}
            />
            )
          : null}
      <div className={styles.questions__wrapper}>
        <Form
          form={form}
          onFinish={() => onSubmit(selectedResponses)}
          className={styles.form}
        >
          {questions.map(question => (
            <QuestionElement
              key={`question-element-${question.question_textId?.id}`}
              type={question.question_textId?.type}
              question={question}
              courses={courses}
              onChangeQuestionResponse={handleChangeQuestionResponse}
              hasme={question.question_textId?.hasMe}
              classMates={classMates}
              selectedResponses={selectedResponses}
              dispatchSelectedResponses={dispatchSelectedResponses}
              filterResponses={filterResponses}
              surveyId={surveyId}
              enrollmentId={enrollmentId}
              organizationType={organizationType}
            />
          ))}
          <Form.Item>
            <div className={styles.buttons_wrapper}>
              {errorCreateResponse && (
                <AlertComponent
                  messageKey='common.resources.survey-response.actions.create.failure'
                  error={errorCreateResponse}
                  type='error'
                />)}
              {errorParticipantsTerminated && (
                <AlertComponent
                  messageKey='common.resources.participant-terminated.actions.read.failure'
                  error={errorParticipantsTerminated}
                  type='error'
                />)}
              <div className={styles.button}>
                <ButtonComponent
                  buttonText={
                      page >= 6 ? t('buttonText2Send') : t('buttonText2')
                    }
                  colorType='primary'
                  htmlType='submit'
                  disabled={!classMates || classMates.length === 0}
                />
              </div>
            </div>
          </Form.Item>
        </Form>
      </div>
    </div>
  )
}

export default QuestionsList
